/**
 * Game screen: the PixiJS map fills the window; the HUD floats above it (only its panels take
 * pointer input). Also wires hotkeys, telegram cards for fresh events, and the single-player
 * auto-pause while the staff menu is open.
 */
import { type RefObject, useEffect, useRef } from 'react';
import { bus } from '../../game/bus';
import { telegramInEveryLocale } from '../../game/format';
import { gameStore, useGame } from '../../game/store';
import { useT } from '../../i18n';
import { getConnection } from '../../net/connection';
import { onCommandResult } from '../../net/router';
import { createMapRenderer, type MapRendererHandle } from '../../render';
import { Dock, Drawer } from './Drawers';
import { FallenNotice } from './FallenNotice';
import { HexReadout } from './HexReadout';
import { useGameHotkeys } from './hotkeys';
import hud from './hud.module.css';
import { hudStore, useHud } from './hudStore';
import { Instructor, TutorialHighlight } from './Instructor';
import { Minimap } from './Minimap';
import { Newspaper } from './Newspaper';
import { CommandNotice, ReconnectBanner } from './Notices';
import { Overlays } from './Overlays';
import { SelectionPanel } from './SelectionPanel';
import { Telegrams } from './Telegrams';
import { Teletype } from './Teletype';
import { TopBar } from './TopBar';

export function useMapRenderer(ref: RefObject<HTMLDivElement | null>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let handle: MapRendererHandle | null = null;
    let cancelled = false;
    createMapRenderer(el)
      .then((h) => {
        if (cancelled) h.destroy();
        else handle = h;
      })
      .catch((err: unknown) => console.error('[game] map renderer failed to start', err));
    return () => {
      cancelled = true;
      handle?.destroy();
    };
  }, [ref]);
}

/** Big moments from each tick become telegram cards (history at game start does not). */
function useTelegramFeed(): void {
  useEffect(
    () =>
      gameStore.subscribe((st, prev) => {
        if (
          st.freshEvents === prev.freshEvents ||
          !prev.view ||
          !st.view ||
          !st.map ||
          !st.grid ||
          st.map !== prev.map
        )
          return;
        const ctx = { view: st.view, map: st.map, grid: st.grid, log: st.log };
        let bad = false;
        let count = 0;
        for (const ev of st.freshEvents) {
          const worded = telegramInEveryLocale(ev, ctx);
          if (!worded) continue;
          hudStore.getState().pushTelegram(worded, ev.tick);
          bad ||= worded.en.tone === 'bad';
          count++;
        }
        if (!count) return;
        bus.emit('sound', { name: 'paper' });
        if (bad) bus.emit('sound', { name: 'alert' });
        window.setTimeout(() => bus.emit('sound', { name: 'stamp' }), 230);
      }),
    [],
  );
}

/** Commands the host rejects flash under the top bar with a buzz. */
function useCommandRejections(): void {
  useEffect(
    () =>
      onCommandResult((_seq, result) => {
        if (result.ok) return;
        hudStore.getState().flashNotice(result.error);
        bus.emit('sound', { name: 'deny' });
      }),
    [],
  );
}

/** Single player: the clock stops while the staff conference (menu) is open. */
function useMenuPause(): void {
  useEffect(() => {
    let pausedByMenu = false;
    return hudStore.subscribe((h, prev) => {
      if (h.overlay === prev.overlay || getConnection()?.kind !== 'local') return;
      const g = gameStore.getState();
      if (h.overlay && !prev.overlay && !g.paused) {
        g.setPaused(true);
        pausedByMenu = true;
      } else if (!h.overlay && pausedByMenu) {
        pausedByMenu = false;
        if (g.view && !g.view.winners) g.setPaused(false);
      }
    });
  }, []);
}

/**
 * Publish the height of the status strip (PAUSED, notices) as `--status-h` on the HUD, so the
 * telegram column starts below it however many strips are showing.
 */
function useStatusHeight(
  status: RefObject<HTMLDivElement | null>,
  hudEl: RefObject<HTMLDivElement | null>,
): void {
  useEffect(() => {
    const el = status.current;
    const root = hudEl.current;
    if (!el || !root) return;
    const publish = () => root.style.setProperty('--status-h', `${el.offsetHeight}px`);
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    publish();
    return () => ro.disconnect();
  }, [status, hudEl]);
}

export function GameScreen() {
  const t = useT();
  const mapRef = useRef<HTMLDivElement>(null);
  const hudRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  useStatusHeight(statusRef, hudRef);
  useMapRenderer(mapRef);
  useGameHotkeys();
  useTelegramFeed();
  useMenuPause();
  useCommandRejections();
  useEffect(() => {
    hudStore.getState().reset();
    return () => hudStore.getState().reset();
  }, []);

  const paused = useGame((g) => g.paused);
  const panel = useHud((h) => h.panel);
  const over = useGame((g) => g.view?.winners != null);

  return (
    <div className={hud.screen}>
      <div ref={mapRef} className={hud.map} />
      <div className={hud.vignette} />
      <div
        ref={hudRef}
        className={hud.hud}
        data-drawer={panel ? (panel === 'research' ? 'wide' : 'narrow') : 'none'}
      >
        <TopBar />
        <div ref={statusRef} className={hud.status}>
          {paused && !over && <div className={hud.paused}>{t('hud.paused')}</div>}
          <ReconnectBanner />
          <CommandNotice />
        </div>
        <Dock />
        <Drawer />
        <SelectionPanel />
        <Telegrams />
        <Teletype />
        <HexReadout />
        <Minimap />
        <Newspaper />
        <Instructor />
        <FallenNotice />
        <Overlays />
      </div>
      <TutorialHighlight />
    </div>
  );
}
